import React, { useState } from "react";
import apiClient from '../services/apiClient';
import { useQuery } from '@tanstack/react-query';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { FormMultiSelect } from '../components/ui';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { formatDateTime } from '../utils/dateUtils';

// ─── Types ────────────────────────────────────────────────────────────────────
type FinancialLog = {
  id: string;
  created_at: string;
  user_id: string;
  username: string;
  email: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  status: string;
  reference_type: string;
  currency: string;
  type: string;
  reference_id: string | null;
  description: string | null;
  affiliate_name?: string | null;
  manager_name?: string | null;
  parent_name?: string | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) => formatDateTime(d);

const fmtAmt = (n: number) =>
  n === 0 ? "0.00" : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ALL_COLUMNS = [
  { id: "sn", label: "SN" },
  { id: "created_at", label: "Date & Time" },
  { id: "user", label: "User" },
  { id: "referred_by", label: "Referred By" },
  { id: "type", label: "Type" },
  { id: "currency", label: "Currency" },
  { id: "amount", label: "Amount" },
  { id: "balance_before", label: "Bal Before" },
  { id: "balance_after", label: "Bal After" },
  { id: "status", label: "Status" },
  { id: "description", label: "Description" },
];

const TX_TYPES = ['deposit', 'withdrawal', 'win', 'bet', 'bonus', 'wallet_credit', 'rakeback_claim', 'campaign_bonus', 'refund'];
// ─── Component ────────────────────────────────────────────────────────────────
const FinancialReports: React.FC = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);


  const [currencies, setCurrencies] = useState<string[]>([]);
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();

  React.useEffect(() => {
    const fetchFilters = async () => {
      try {
        const token = localStorage.getItem("token");
        const currenciesRes = await apiClient.get(`/api/currencies`);
        if (currenciesRes.data?.success && Array.isArray(currenciesRes.data.currencies)) {
          setCurrencies(currenciesRes.data.currencies.map((c: any) => c.code.toUpperCase()));
        } else {
          setCurrencies(['INR', 'USDTTRC20', 'USDTMATIC', 'BTC', 'ETH']);
        }
      } catch (err) {
        console.error("Failed to fetch filters", err);
        setCurrencies(['INR', 'USDTTRC20', 'USDTMATIC', 'BTC', 'ETH']);
      }
    };
    fetchFilters();
  }, []);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['financial_reports', page, limit, search, typeFilter, currencyFilter, startDate, endDate],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      let url = `/getFinancialLogsReport?page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (typeFilter.length > 0) url += `&typeFilter=${encodeURIComponent(typeFilter.join(','))}`;
      if (currencyFilter.length > 0) url += `&currencyFilter=${encodeURIComponent(currencyFilter.join(','))}`;
      if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

      const res = await apiClient.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const responseData = res.data?.data || [];
      const pagination = res.data?.pagination || { total: 0, totalPages: 1 };

      const mapped: FinancialLog[] = responseData.map((u: any) => ({
        id: String(u.id),
        created_at: u.created_at ?? "",
        user_id: String(u.user_id),
        username: u.username ?? "—",
        email: u.email ?? "—",
        amount: Number(u.amount) || 0,
        balance_before: Number(u.balance_before) || 0,
        balance_after: Number(u.balance_after) || 0,
        status: u.status ?? "unknown",
        reference_type: u.reference_type ?? "—",
        currency: u.currency_code ?? "—",
        type: u.type ?? "—",
        reference_id: u.reference_id ?? null,
        description: u.description ?? null,
        affiliate_name: u.affiliate_name ?? null,
        manager_name: u.manager_name ?? null,
        parent_name: u.parent_name ?? null,
      }));

      return { logs: mapped, pagination };
    }
  });

  const logs = reportData?.logs || [];
  const total = reportData?.pagination?.total || 0;
  const totalPages = reportData?.pagination?.totalPages || 1;

  // ─── CSV Export ───────────────────────────────────────────────────────────────
  const exportCSV = async () => {
    const token = localStorage.getItem("token");
    let url = `/getFinancialLogsReport?page=1&limit=50000`; // Fetch up to 50k for CSV
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (typeFilter.length > 0) url += `&typeFilter=${encodeURIComponent(typeFilter.join(','))}`;
    if (currencyFilter.length > 0) url += `&currencyFilter=${encodeURIComponent(currencyFilter.join(','))}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;


    try {
      const res = await apiClient.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const exportData = res.data?.data || [];

      const headers = ALL_COLUMNS.map(c => c.label);

      const rows = exportData.map((u: any, i: any) => {
        return ALL_COLUMNS.map(c => {
          switch (c.id) {
            case "sn": return i + 1;
            case "created_at": return fmtDate(u.created_at);
            case "user": return `[${u.user_id}] ${u.username} (${u.email})`;
            case "referred_by": return u.parent_name || "—";
            case "type": return u.type;
            case "currency": return u.currency;
            case "amount": return Number(u.amount).toFixed(2);
            case "balance_before": return Number(u.balance_before).toFixed(2);
            case "balance_after": return Number(u.balance_after).toFixed(2);
            case "status": return u.status;
            case "description": return u.description || u.reference_type;
            default: return "";
          }
        });
      });

      const csv =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows]
          .map(r =>
            r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(",")
          )
          .join("\n");

      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csv));
      link.setAttribute("download", `financial_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Failed to export", e);
      alert("Failed to export report");
    }
  };

  const getTypeStyle = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'win': return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
      case 'deposit': return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
      case 'bonus': return 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
      case 'campaign_bonus': return 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
      case 'rakeback_claim': return 'bg-purple-500/10 text-purple-400 border border-purple-500/20';
      case 'wallet_credit': return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      case 'bet': return 'bg-rose-500/10 text-rose-400 border border-rose-500/20';
      case 'withdrawal': return 'bg-red-500/10 text-red-400 border border-red-500/20';
      case 'refund': return 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
    }
  };

  // ─── Columns for StakeTable ────────────────────────────────────────────────────
  const columns: Column[] = ALL_COLUMNS.map(c => {
      const col: Column = { key: c.id, label: c.label };
      switch (c.id) {
        case "sn": col.render = (_, __, i) => ((page - 1) * limit) + i + 1; break;
        case "created_at": col.render = (_, u: FinancialLog) => <span className="text-gray-300 whitespace-nowrap">{fmtDate(u.created_at)}</span>; break;
        case "user": col.render = (_, u: FinancialLog) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-white">{u.username}</span>
            <span className="text-xs text-gray-400">UID: {u.user_id}</span>
          </div>
        ); break;
        case "referred_by": col.render = (_, u: FinancialLog) => (
          <span className="text-gray-300 font-medium">{u.parent_name || "—"}</span>
        ); break;
        case "type": col.render = (_, u: FinancialLog) => (
          <span className={`px-2 py-1 rounded-md text-[11px] font-semibold tracking-wide uppercase ${getTypeStyle(u.type)}`}>
            {u.type.replace('_', ' ')}
          </span>
        ); break;
        case "currency": col.render = (val) => <span className="font-bold text-gray-200">{val}</span>; col.textAlign = 'center'; break;
        case "amount": col.render = (_, u: FinancialLog) => {
          const rawAmt = Number(u.amount);
          const isPositive = rawAmt > 0;
          const isNegative = rawAmt < 0;
          return (
            <span className={`font-bold ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-gray-400'}`}>
              {isPositive ? '+' : isNegative ? '-' : ''}{fmtAmt(Math.abs(rawAmt))}
            </span>
          );
        }; break;
        case "balance_before": col.render = (_, u: FinancialLog) => <span className="text-gray-400">{fmtAmt(u.balance_before)}</span>; break;
        case "balance_after": col.render = (_, u: FinancialLog) => <span className="text-white font-medium">{fmtAmt(u.balance_after)}</span>; break;
        case "status": col.render = (val) => (
          <span className={`px-2 py-1 rounded-md text-[11px] font-semibold capitalize ${val === 'completed' || val === 'accepted' ? 'text-emerald-400' : val === 'pending' ? 'text-amber-400' : 'text-red-400'}`}>
            {val}
          </span>
        ); break;
        case "description": col.render = (_, u: FinancialLog) => (
          <div className="flex flex-col max-w-[200px]">
            <span className="text-sm text-gray-300 truncate" title={u.description || ''}>{u.description || u.reference_type}</span>
            {u.reference_id && <span className="text-[10px] text-gray-500 font-mono">Ref: {u.reference_id}</span>}
          </div>
        ); break;
      }
      return col;
    });

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#1E2437] p-4 rounded-xl mb-6 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-white">Financial &amp; Bonus Logs</h2>
          <p className="text-sm text-gray-400">View detailed flow of funds, bonuses, and rakeback distribution.</p>
        </div>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex lg:hidden items-center gap-2 px-4 py-2 bg-[#151929] border border-slate-700 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
          {(typeFilter.length + currencyFilter.length) > 0 && (
            <span className="ml-1 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {typeFilter.length + currencyFilter.length}
            </span>
          )}
        </button>
      </div>
      
      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <StakeTable
          isLoading={isLoading}
          title=""
          columns={columns}
          data={logs}
          searchPlaceholder="Search by UID, username, email, or Ref ID..."
          onSearch={(val) => { setSearch(val); setPage(1); }}
          emptyMessage="No transactions match the current filters."
          hasSidebarMargin={false}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          actions={
            <button
              onClick={exportCSV}
              className="w-full sm:w-auto justify-center bg-[#646ECD] hover:bg-[#525ab5] text-white text-sm font-medium py-2 px-3 rounded-md flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Excel
            </button>
          }
          bottomActions={
            <div className={`flex flex-wrap gap-3 items-end ${showFilters ? 'flex' : 'hidden lg:flex'}`}>
              <div className="w-full sm:w-[180px] shrink-0">
                <FormMultiSelect
                  label="Transaction Type"
                  selectedValues={typeFilter}
                  onChange={(vals) => { setTypeFilter(vals); setPage(1); }}
                  placeholder="All Types"
                  options={TX_TYPES.map(t => ({ value: t, label: t.replace('_', ' ').toUpperCase() }))}
                />
              </div>

              <div className="w-full sm:w-[160px] shrink-0">
                <FormMultiSelect
                  label="Currency"
                  selectedValues={currencyFilter}
                  onChange={(vals) => { setCurrencyFilter(vals); setPage(1); }}
                  placeholder="All Currencies"
                  options={currencies.map(c => ({ value: c, label: c }))}
                />
              </div>

              <div className="w-full sm:w-[180px] shrink-0 z-10">
                <DateRangeFilter 
                  label="Date Range"
                  onFilterChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                    setPage(1);
                  }} 
                />
              </div>

              {(search || typeFilter.length > 0 || currencyFilter.length > 0 || startDate || endDate) && (
                <button
                  onClick={() => { 
                    setSearch(""); 
                    setPage(1); 
                    setTypeFilter([]); 
                    setCurrencyFilter([]); 
                    setStartDate(""); 
                    setEndDate(""); 
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all h-[38px]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reset
                </button>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default FinancialReports;
